import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiUrl from "../Environment/ApiUrl";
import { useToast } from "../Toast/ToastContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

function Purchase() {
  const toast = useToast();
  const navigate = useNavigate();

  // State for notifications
  const [notifications, setNotifications] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPurchaseId, setSelectedPurchaseId] = useState(null);

  // State for fetching data
  const [responseData, setResponseData] = useState([]);
  const [loading, setLoading] = useState(false);

  // State for delete modal
  const [deleteShowModals, setDeleteShowModals] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [deleteName, setDeleteName] = useState("");

  // State for pagination and search
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showAllItems, setShowAllItems] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // ─── Helper Functions ──────────────────────────────────────────────────────

  // Function to format date to dd-mm-yyyy
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Return original if invalid
      }
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString; // Return original if parsing fails
    }
  };

  // Function to get supplier display name with Tamil
  const getSupplierDisplayName = (supplier) => {
    if (!supplier) return "";
    if (supplier.name_tamil) {
      return `${supplier.name} / ${supplier.name_tamil}`;
    }
    return supplier.name || "";
  };

  // Function to get supplier name for search (includes both English and Tamil)
  const getSupplierSearchText = (supplier) => {
    if (!supplier) return "";
    if (supplier.name_tamil) {
      return `${supplier.name} ${supplier.name_tamil}`.toLowerCase();
    }
    return (supplier.name || "").toLowerCase();
  };

  const handleprint = (id, purchase_id) => {
    navigate(`/purchase-bill/${purchase_id}`);
  };

  // ─── Notification Functions ──────────────────────────────────────────────

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/notification.php`);
      setNotifications(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching notifications:", error.message);
      toast.error("Failed to fetch notifications", 3000, "topRight");
    }
  };

  const checkNotificationStatus = (purchaseId, buttonValue) => {
    const userId = localStorage.getItem("username");
    return notifications.some(
      (notif) =>
        notif.table_id === purchaseId.toString() &&
        notif.button_value === buttonValue &&
        notif.status === "accepted" &&
        notif.employe_id === userId &&
        notif.viewed === "false"
    );
  };

  const hasPendingNotification = (purchaseId, buttonValue) => {
    const userId = localStorage.getItem("username");
    return notifications.some(
      (notif) =>
        notif.table_id === purchaseId.toString() &&
        notif.button_value === buttonValue &&
        notif.status === "requested" &&
        notif.employe_id === userId &&
        notif.viewed === "false"
    );
  };

  const openNotificationModal = (id, action) => {
    setSelectedPurchaseId(id);
    setActionType(action);
    setShowModal(true);
  };

  const closeNotificationModal = () => {
    setShowModal(false);
    setSelectedPurchaseId(null);
    setActionType("");
    setIsSubmitting(false);
  };

  const handleNotificationSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isSubmitting) return;
    setIsSubmitting(true);

    if (hasPendingNotification(selectedPurchaseId, actionType)) {
      toast.warning(
        "A pending request for this action already exists!",
        3000,
        "topRight"
      );
      closeNotificationModal();
      return;
    }

    try {
      const userId = localStorage.getItem("username");
      const currentTime = new Date()
        .toISOString()
        .slice(0, 19)
        .replace("T", " ");

      const formData = {
        table_name: "purchase_details",
        table_id: selectedPurchaseId,
        employe_id: userId || "",
        status: "requested",
        button_value: actionType,
        time: currentTime,
        viewed: "false",
        created_date: currentTime,
        updated_by: userId || "",
        updated_date: currentTime,
      };

      await axios.post(`${ApiUrl}/Api/notification.php`, formData, {
        headers: { "Content-Type": "application/json" },
      });

      toast.success(
        `${actionType} request sent successfully!`,
        3000,
        "topRight"
      );
      closeNotificationModal();
      fetchNotifications();
    } catch (error) {
      console.error("Error submitting notification:", error.message);
      toast.error(
        "Error submitting notification. Please try again.",
        3000,
        "topRight"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Data Fetching ─────────────────────────────────────────────────────────

  const fetchData = async () => {
    try {
      setLoading(true);
      const localRole = localStorage.getItem("role");
      const localUsername = localStorage.getItem("username");
      const response = await axios.get(
        localRole === "Employee"
          ? `${ApiUrl}/Api/purchase_details.php?employee_id=${localUsername}`
          : `${ApiUrl}/Api/purchase_details.php`
      );
      console.log("Response from server:", response.data);
      setResponseData(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching data:", error.message);
      setResponseData([]);
      toast.error("Failed to fetch data", 3000, "topRight");
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  // ─── CRUD Operations ──────────────────────────────────────────────────────

  const handleUpdatePurchase = (id) => {
    const userRole = localStorage.getItem("role");
    if (userRole === "Employee" || userRole === "Manager") {
      if (checkNotificationStatus(id, "Edit")) {
        navigate(`/update-purchase/${id}`);
      } else {
        openNotificationModal(id, "Edit");
      }
    } else {
      navigate(`/update-purchase/${id}`);
    }
  };

  const handleOpenModal = (id, purchase_id) => {
    const userRole = localStorage.getItem("role");
    if (userRole === "Employee" || userRole === "Manager") {
      if (checkNotificationStatus(id, "Delete")) {
        setDeleteId(id);
        setDeleteName(purchase_id);
        setDeleteShowModals(true);
      } else {
        openNotificationModal(id, "Delete");
      }
    } else {
      setDeleteId(id);
      setDeleteName(purchase_id);
      setDeleteShowModals(true);
    }
  };

  const handleCloseModal = () => {
    setDeleteShowModals(false);
    setDeleteId("");
    setDeleteName("");
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(`${ApiUrl}/Api/purchase_details.php/${deleteId}`);

      const notification = notifications.find(
        (notif) =>
          notif.table_id === deleteId.toString() &&
          notif.button_value === "Delete" &&
          notif.viewed === "false"
      );
      if (notification) {
        const currentTime = new Date()
          .toISOString()
          .slice(0, 19)
          .replace("T", " ");
        const notificationPayload = {
          id: notification.id,
          viewed: "true",
          updated_by: localStorage.getItem("username"),
          updated_date: currentTime,
          table_name: notification.table_name,
          table_id: notification.table_id,
          employe_id: notification.employe_id,
          status: notification.status,
          button_value: notification.button_value,
          time: notification.time,
          created_date: notification.created_date,
        };

        await axios.put(
          `${ApiUrl}/Api/notification.php/${notification.id}`,
          notificationPayload,
          {
            headers: { "Content-Type": "application/json" },
          }
        );
      }

      toast.success("Deleted successfully!", 3000, "topRight");
      handleCloseModal();
      fetchData();
      fetchNotifications();
    } catch (error) {
      console.error("Error deleting data:", error.message);
      toast.error("Failed to delete data", 3000, "topRight");
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  // ─── Pagination and Search ────────────────────────────────────────────────

  const filteredData = responseData.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    const supplierSearchText = getSupplierSearchText(item.supplier);
    
    return (
      (item.purchase_id &&
        item.purchase_id.toLowerCase().includes(searchLower)) ||
      (item.purchase_date &&
        formatDate(item.purchase_date).toLowerCase().includes(searchLower)) ||
      (item.supplier_id && item.supplier_id.toString().includes(searchLower)) ||
      (item.total_pure &&
        item.total_pure.toLowerCase().includes(searchLower)) ||
      (item.paid && item.paid.toString().includes(searchLower)) ||
      (item.close && item.close.toString().includes(searchLower)) ||
      supplierSearchText.includes(searchLower)
    );
  });

  const totalItems = filteredData.length;
  const totalPages = showAllItems ? 1 : Math.ceil(totalItems / itemsPerPage);

  const currentItems = showAllItems
    ? filteredData
    : filteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      );

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === "All") {
      setShowAllItems(true);
      setCurrentPage(1);
    } else {
      setShowAllItems(false);
      setItemsPerPage(Number(value));
      setCurrentPage(1);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 2;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <li
          key={1}
          className={`page-item ${currentPage === 1 ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(1)}>
            1
          </button>
        </li>
      );
      if (startPage > 2) {
        pages.push(
          <li key="start-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(i)}>
            {i}
          </button>
        </li>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <li key="end-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }
      pages.push(
        <li
          key={totalPages}
          className={`page-item ${currentPage === totalPages ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        </li>
      );
    }

    return pages;
  };

  // ─── Export Functions ─────────────────────────────────────────────────────

  const handlePrint = () => {
    try {
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
          <head>
            <title>Purchase Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f2f2f2; font-weight: bold; }
              h1 { text-align: center; color: #333; }
              .print-date { text-align: right; margin-bottom: 20px; font-size: 12px; }
              .table-secondary { background-color: #e2e3e5; }
              .table-danger { background-color: #f8d7da; }
              .table-info { background-color: #cff4fc; }
            </style>
          </head>
          <body>
            <h1>Purchase Report</h1>
            <div class="print-date">Generated on: ${formatDate(
        new Date()
      )}</div>
            <table>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Purchase No</th>
                  <th>Purchase Date</th>
                  <th>Supplier Name</th>
                  <th>Total Items</th>
                  <th>Total Amount</th>
                  <th>Total Pure(g)</th>
                </tr>
              </thead>
              <tbody>
                ${filteredData
          .map((item, index) => {
            const userId = localStorage.getItem("username");
            const notification = notifications.find(
              (notif) =>
                notif.table_id === item.id.toString() &&
                notif.employe_id === userId &&
                notif.viewed === "false"
            );
            let rowClass = "";
            if (notification) {
              switch (notification.status) {
                case "accepted":
                  rowClass = "table-secondary";
                  break;
                case "rejected":
                  rowClass = "table-danger";
                  break;
                case "requested":
                  rowClass = "table-info";
                  break;
              }
            }
            const supplierName = getSupplierDisplayName(item.supplier);
            return `
                      <tr class="${rowClass}">
                        <td>${index + 1}</td>
                        <td>${item.purchase_id || ""}</td>
                        <td>${formatDate(item.purchase_date) || ""}</td>
                        <td>${supplierName}</td>
                        <td>${item.items?.length || 0}</td>
                        <td>₹${item.paid || 0}</td>
                        <td>${item.total_pure || ""}</td>
                      </tr>
                    `;
          })
          .join("")}
              </tbody>
            </table>
            <script>
              window.onload = function() { window.print(); window.close(); }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
      toast.success("Print dialog opened!", 3000, "topRight");
    } catch (error) {
      console.error("Error printing:", error);
      toast.error("Error printing. Please try again", 3000, "topRight");
    }
  };

  const exportPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text("Purchase Report", 14, 22);
      doc.setFontSize(10);
      doc.text(`Generated on: ${formatDate(new Date())}`, 14, 35);

      const tableColumn = [
        "S.No",
        "Purchase No",
        "Purchase Date",
        "Supplier Name",
        "Total Items",
        "Total Amount",
        "Total Pure",
      ];

      const tableRows = filteredData.map((item, index) => {
        const userId = localStorage.getItem("username");
        const notification = notifications.find(
          (notif) =>
            notif.table_id === item.id.toString() &&
            notif.employe_id === userId &&
            notif.viewed === "false"
        );
        let rowStyle = {};
        if (notification) {
          switch (notification.status) {
            case "accepted":
              rowStyle = { fillColor: [226, 227, 229] };
              break;
            case "rejected":
              rowStyle = { fillColor: [248, 215, 218] };
              break;
            case "requested":
              rowStyle = { fillColor: [207, 244, 252] };
              break;
          }
        }
        const supplierName = getSupplierDisplayName(item.supplier);
        return [
          index + 1,
          item.purchase_id || "",
          formatDate(item.purchase_date) || "",
          supplierName,
          item.items?.length || 0,
          `₹${item.paid || 0}`,
          item.total_pure || "",
          rowStyle,
        ];
      });

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows.map((row) => row.slice(0, -1)),
        startY: 45,
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: {
          fillColor: [66, 139, 202],
          textColor: 255,
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 25 },
          2: { cellWidth: 25 },
          3: { cellWidth: 35 },
          4: { cellWidth: 20 },
          5: { cellWidth: 25 },
          6: { cellWidth: 20 },
        },
        didParseCell: (data) => {
          if (data.section === "body") {
            const rowIndex = data.row.index;
            const rowStyle =
              tableRows[rowIndex][tableRows[rowIndex].length - 1];
            if (rowStyle) {
              data.cell.styles = { ...data.cell.styles, ...rowStyle };
            }
          }
        },
      });

      doc.save(`purchase-report-${formatDate(new Date())}.pdf`);
      toast.success("PDF exported successfully!", 3000, "topRight");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Error exporting PDF. Please try again.", 3000, "topRight");
    }
  };

  const copyToClipboard = () => {
    try {
      const headers = [
        "S.No",
        "Purchase No",
        "Purchase Date",
        "Supplier Name",
        "Total Items",
        "Total Amount",
        "Total Pure",
      ];

      const rows = filteredData.map((item, index) => [
        index + 1,
        item.purchase_id || "",
        formatDate(item.purchase_date) || "",
        getSupplierDisplayName(item.supplier),
        item.items?.length || 0,
        `₹${item.paid || 0}`,
        item.total_pure || "",
      ]);

      const csvData = [headers, ...rows]
        .map((row) => row.map((field) => `"${field}"`).join("\t"))
        .join("\n");

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(csvData)
          .then(() => {
            toast.success("Data copied to clipboard!", 3000, "topRight");
          })
          .catch((err) => {
            console.error("Clipboard API failed:", err);
            fallbackCopy(csvData);
          });
      } else {
        fallbackCopy(csvData);
      }
    } catch (error) {
      console.error("Unexpected error copying to clipboard:", error);
      toast.error("Failed to copy data", 3000, "topRight");
    }
  };

  const fallbackCopy = (text) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.top = 0;
      textArea.style.left = 0;
      textArea.style.width = "2em";
      textArea.style.height = "2em";
      textArea.style.padding = 0;
      textArea.style.border = "none";
      textArea.style.outline = "none";
      textArea.style.boxShadow = "none";
      textArea.style.background = "transparent";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      if (successful) {
        toast.success("Data copied to clipboard!", 3000, "topRight");
      } else {
        toast.error("Failed to copy data", 3000, "topRight");
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);
      toast.error("Failed to copy data", 3000, "topRight");
    }
  };

  const exportCSV = () => {
    try {
      const headers = [
        "S.No",
        "Purchase No",
        "Purchase Date",
        "Supplier Name",
        "Total Items",
        "Total Amount",
        "Total Pure",
      ];
      const rows = filteredData.map((item, index) => [
        index + 1,
        item.purchase_id || "",
        formatDate(item.purchase_date) || "",
        getSupplierDisplayName(item.supplier),
        item.items?.length || 0,
        item.paid || 0,
        item.total_pure || "",
      ]);

      const csvContent = [
        headers.join(","),
        ...rows.map((row) =>
          row
            .map((field) =>
              typeof field === "string" && field.includes(",")
                ? `"${field}"`
                : field
            )
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `purchase-report-${formatDate(new Date())}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("CSV exported successfully!", 3000, "topRight");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Error exporting CSV. Please try again.", 3000, "topRight");
    }
  };

  const exportExcel = () => {
    try {
      const worksheetData = [
        [
          "S.No",
          "Purchase No",
          "Purchase Date",
          "Supplier Name",
          "Total Items",
          "Total Amount",
          "Total Pure",
        ],
        ...filteredData.map((item, index) => [
          index + 1,
          item.purchase_id || "",
          formatDate(item.purchase_date) || "",
          getSupplierDisplayName(item.supplier),
          item.items?.length || 0,
          item.paid || 0,
          item.total_pure || "",
        ]),
      ];

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const colWidths = [
        { wch: 8 },
        { wch: 15 },
        { wch: 15 },
        { wch: 35 },
        { wch: 12 },
        { wch: 15 },
        { wch: 12 },
      ];
      worksheet["!cols"] = colWidths;

      const headerRange = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!worksheet[cellAddress]) continue;
        worksheet[cellAddress].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "DDDDDD" } },
          alignment: { horizontal: "center" },
        };
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, "Purchase Report");
      XLSX.writeFile(
        workbook,
        `purchase-report-${formatDate(new Date())}.xlsx`
      );
      toast.success("Excel file exported successfully!", 3000, "topRight");
    } catch (error) {
      console.error("Error exporting Excel:", error);
      toast.error("Error exporting Excel. Please try again.", 3000, "topRight");
    }
  };

  // ─── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchData();
    fetchNotifications();
    const interval = setInterval(() => {
      fetchData();
      fetchNotifications();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-message">
            <div className="loadinggif"></div>
          </div>
        </div>
      )}

      <div className="container-fluid">
        <div className="d-flex mb-2">
          <h6 className="card-title m-2">Purchase</h6>
          <button
            className="btn btn-primary btn-sm rounded-3"
            onClick={() => navigate("/add-purchase")}
          >
            Add <span className="ms-2">+</span>
          </button>
        </div>

        <div className="card shadow-sm mb-5">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex">
                <label className="form-label text-dark mt-2">Show : </label>
                <div>
                  <select
                    className="form-control mx-2"
                    onChange={handleItemsPerPageChange}
                    value={showAllItems ? "All" : itemsPerPage}
                  >
                    <option value="All">All</option>
                    {[5, 10, 20, 100, 200, 500, 1000].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="d-flex justify-content-center">
                <button
                  className="btn btn-sm btn-info text-white mx-1"
                  onClick={handlePrint}
                >
                  Print
                </button>
                <button
                  className="btn btn-sm btn-info text-white mx-1"
                  onClick={exportPDF}
                >
                  PDF
                </button>
                <button
                  className="btn btn-sm btn-info text-white mx-1"
                  onClick={copyToClipboard}
                >
                  Copy
                </button>
                <button
                  className="btn btn-sm btn-info text-white mx-1"
                  onClick={exportCSV}
                >
                  CSV
                </button>
                <button
                  className="btn btn-sm btn-info text-white mx-1"
                  onClick={exportExcel}
                >
                  Excel
                </button>
              </div>

              <div className="search-container">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search"
                    aria-label="Search"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="card tableCard p-2">
              <div className="table-responsive">
                <table className="table">
                  <thead className="bg-light">
                    <tr>
                      <th>S.No</th>
                      <th>Purchases ID</th>
                      <th>Purchases_Date</th>
                      <th style={{ minWidth: "200px" }}>Supplier Name</th>
                      <th>Total Items</th>
                      <th>Total Pure</th>
                      <th>Total Amount</th>
                      <th>Balance</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.length > 0 ? (
                      currentItems.map((item, index) => {
                        const userId = localStorage.getItem("username");
                        const notification = notifications.find(
                          (notif) =>
                            notif.table_id === item.id.toString() &&
                            notif.viewed === "false"
                        );
                        let rowClass = "";
                        if (notification) {
                          switch (notification.status) {
                            case "accepted":
                              rowClass = "table-secondary";
                              break;
                            case "rejected":
                              rowClass = "table-danger";
                              break;
                            case "requested":
                              rowClass = "table-info";
                              break;
                          }
                        }
                        const supplierName = getSupplierDisplayName(item.supplier);
                        return (
                          <tr key={item.id} className={rowClass}>
                            <td>{index + 1}</td>
                            <td>{item.purchase_id}</td>
                            <td>{formatDate(item.purchase_date)}</td>
                            <td>
                              {supplierName || item.supplier?.name || "-"}
                            </td>
                            <td>{item.items?.length || 0}</td>
                            <td>{item.pure || "-"}</td>
                            <td>{item.paid || "-"}</td>
                            <td>{item.close}</td>
                            <td>
                              <button
                                className="btn btn-light btn-sm mx-1"
                                onClick={() => handleUpdatePurchase(item.id)}
                              >
                                <i className="fa-solid fa-pen-to-square text-primary fs-6"></i>
                              </button>
                              <button
                                className="btn btn-light btn-sm"
                                onClick={() =>
                                  handleOpenModal(item.id, item.purchase_id)
                                }
                              >
                                <i className="fa-solid fa-trash text-danger fs-6"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-light"
                                onClick={() =>
                                  handleprint(item.id, item.purchase_id)
                                }
                              >
                                <i className="fa-solid fa-print text-secondary fs-6"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center">
                          No purchases found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="row">
                <div className="col-lg-6">
                  <p className="text-muted">
                    Showing{" "}
                    {showAllItems ? 1 : (currentPage - 1) * itemsPerPage + 1} to{" "}
                    {showAllItems
                      ? totalItems
                      : Math.min(currentPage * itemsPerPage, totalItems)}{" "}
                    of {totalItems} entries
                  </p>
                </div>
                <div className="col-lg-6">
                  <nav aria-label="Page navigation">
                    <ul className="pagination justify-content-end">
                      <li
                        className={`page-item ${
                          currentPage === 1 ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(1)}
                          aria-label="First"
                        >
                          <span aria-hidden="true">««</span>
                        </button>
                      </li>
                      <li
                        className={`page-item ${
                          currentPage === 1 ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                          aria-label="Previous"
                        >
                          <span aria-hidden="true">«</span>
                        </button>
                      </li>
                      {renderPageNumbers()}
                      <li
                        className={`page-item ${
                          currentPage === totalPages || totalPages === 0
                            ? "disabled"
                            : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                          aria-label="Next"
                        >
                          <span aria-hidden="true">»</span>
                        </button>
                      </li>
                      <li
                        className={`page-item ${
                          currentPage === totalPages || totalPages === 0
                            ? "disabled"
                            : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(totalPages)}
                          aria-label="Last"
                        >
                          <span aria-hidden="true">»»</span>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Modal */}
      <div
        className={`modal fade ${showModal ? "show" : ""}`}
        style={{
          display: showModal ? "block" : "none",
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
        tabIndex="-1"
        aria-labelledby="notificationModalLabel"
        aria-hidden={!showModal}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h6
                className="modal-title text-primary"
                id="notificationModalLabel"
              >
                Send {actionType} Request to Admin
              </h6>
              <button
                type="button"
                className="btn-close"
                style={{ fontSize: "10px" }}
                onClick={closeNotificationModal}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleNotificationSubmit}>
                <p className="text-center">
                  Do you want to request to {actionType.toLowerCase()} this
                  purchase?
                </p>
                <div className="text-center mt-4">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm px-4 me-2"
                    onClick={closeNotificationModal}
                  >
                    No
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm px-4"
                    disabled={isSubmitting}
                  >
                    Yes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <div
        className={`modal fade ${deleteShowModals ? "show" : ""}`}
        style={{
          display: deleteShowModals ? "block" : "none",
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
        tabIndex="-1"
        aria-labelledby="deleteConfirmModalLabel"
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-confirm">
          <div className="modal-content">
            <div className="modal-body text-center">
              <div className="d-flex justify-content-center mb-3">
                <div
                  className="icon-box d-flex justify-content-center align-items-center bg-light rounded-circle"
                  style={{ width: "80px", height: "80px", margin: "0 auto" }}
                >
                  <i className="bi bi-exclamation-triangle text-danger fs-2"></i>
                </div>
              </div>
              <h4 className="modal-title mb-3 text-center">Are you sure?</h4>
              <h6 className="mb-4 text-center">
                Do you really want to delete this
                <span className="text-primary">({deleteName})</span>?
              </h6>
              <div className="d-flex btn-group">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm flex-grow-1"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm flex-grow-1 mx-2"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {(showModal || deleteShowModals) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </>
  );
}

export default Purchase;